import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { apiClient } from '@/lib/apiClient';
import { CommandInputForm } from '@/components/commands/CommandInputForm';
import { bulkCommandRequest, type BulkCommandConfig, type BulkCommandRun } from '@/lib/bulkCommand';
import type { BulkEditSummary, BulkOperationResult } from '@/lib/bulkEdit';
import { useBulkRun } from './useBulkRun';
import { BulkWarningsAlert } from './BulkWarningsAlert';

interface BulkCommandDialogProps {
  /** Entity name, for labels and translations (e.g. `Order`). */
  entity: string;
  command: BulkCommandConfig;
  /** The rows to run it on, each with the stamp the grid loaded it with, and the selected rows left out. */
  run: BulkCommandRun;
  onClose: () => void;
  /** The run is over: every row applied or refused. The list refreshes and reports the summary. */
  onCompleted: (summary: BulkEditSummary) => void;
}

/**
 * A command on the selected rows (docs/15-designspecs/Bulk-Edit-And-Actions.md §4.3, §8.3, §8.4): the command's
 * declared input asked once, then one request that runs the command on every row that can run it. The selected rows
 * the menu found unable are left out, and the dialog says how many and why. A row whose rules raise warnings
 * keeps the dialog open with the warnings grouped by rule, and "Acknowledge and run these N" re-runs exactly those
 * rows (D14).
 */
export function BulkCommandDialog({ entity, command, run, onClose, onCompleted }: BulkCommandDialogProps) {
  const { rows } = run;
  const { t } = useTranslation();
  const [input, setInput] = useState<Record<string, unknown>>({});
  // A command without input has nothing to fill in; one with input waits until its form accepts it.
  const [inputValid, setInputValid] = useState(!command.input);
  const { running, requestErrors, warned, start, acknowledgeAndRun, close } = useBulkRun<Record<string, unknown>>(
    async (selection, acknowledgedWarnings, given) => (await apiClient.post<BulkOperationResult>(
      command.endpoint,
      bulkCommandRequest(selection, given, acknowledgedWarnings),
    )).data,
    onCompleted,
  );
  // While a run waits on its warnings the input is held: the warned rows are re-run with what the others got.
  const locked = running || warned !== null;

  return (
    <Dialog open onClose={running ? undefined : () => close(onClose)} maxWidth="sm" fullWidth>
      <DialogTitle>{t('bulkCommand.title', { command: command.label, count: rows.length })}</DialogTitle>
      <DialogContent dividers>
        {run.leftOut > 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <div>{t('bulkCommand.leftOut', { command: command.label, count: run.leftOut })}</div>
            {run.reasons.map((reason) => <div key={reason}>{reason}</div>)}
          </Alert>
        )}

        {requestErrors.length > 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {requestErrors.map((message) => <div key={message}>{message}</div>)}
          </Alert>
        )}

        {warned && <BulkWarningsAlert summary={warned} running={running} onAcknowledge={acknowledgeAndRun} />}

        {command.input ? (
          <CommandInputForm
            entity={entity}
            form={command.input}
            data={input}
            readonly={locked}
            onChange={(next, valid) => {
              setInput((current) => (Object.is(current, next) ? current : next));
              setInputValid(valid);
            }}
          />
        ) : (
          <Typography variant="body2">
            {t('grid.confirmBulkCommand', { command: command.label, count: rows.length })}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => close(onClose)} disabled={running}>{t('common.cancel')}</Button>
        <Button
          variant="contained"
          disabled={locked || !inputValid || rows.length === 0}
          startIcon={running ? <CircularProgress size={16} color="inherit" /> : undefined}
          onClick={() => start(rows, input)}
        >
          {t('bulkCommand.run', { count: rows.length })}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
